import { type LocationData } from '@carvana/merch-geolocation';
import { constructMetadata, SeoData } from '@carvana/traffic';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type React from 'react';
import { useUserLocation } from '~/context/common/UserContext';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import RouteContext from '~/context/srp/RouteContext';
import { VanityUrlWithPath } from '~/contracts/srp';

function getGeolocationInfo(pathname: string, location: LocationData) {
  const regexMatchUrlWithLocation = /[-/]{1}in-([-\w]+)-(\w{2})\b/;
  const found = pathname.match(regexMatchUrlWithLocation);

  return found?.length === 3 ? location : undefined;
}

type ContextType = {
  seoData: SeoData;
  setChanged: () => void;
  vanityUrl?: VanityUrlWithPath;
};

const defaultContext: ContextType = {
  seoData: {
    flags: {
      hasCvnaid: false,
      isAllModels: false,
      isMultiMake: false,
      isNoMake: false,
      isSingleModel: false
    },
    seoH1: ''
  },
  setChanged() {},
  vanityUrl: undefined
};

const SEOContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  vanityUrl?: VanityUrlWithPath;
}>;

export function SEOContextProvider(props: OwnProps) {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const { currentUrl } = useContext(RouteContext);
  const userLocation = useUserLocation();

  const [didChange, setDidChange] = useState(false);
  const setChanged = useCallback(() => {
    setDidChange(true);
  }, []);

  const seoData = useMemo(
    () =>
      constructMetadata(
        appliedFilters,
        getGeolocationInfo(currentUrl, userLocation)
      ),
    [appliedFilters, currentUrl, userLocation]
  );

  const value = useMemo<ContextType>(
    () => ({
      seoData,
      setChanged,
      vanityUrl: didChange ? undefined : props.vanityUrl
    }),
    [didChange, props.vanityUrl, seoData, setChanged]
  );

  return (
    <SEOContext.Provider value={value}>{props.children}</SEOContext.Provider>
  );
}

SEOContextProvider.displayName = 'SEOContextProvider';

export default SEOContext;
